
import { Component, Prop, Vue } from 'bits/vue'
import OzBaseButton, { OzBaseButtonHrefMode } from 'library/v4/components/OzBaseButton.vue'

export enum OzContainedButtonColorScheme {
  Bare = 'Bare',
  Primary = 'Primary',
  PrimaryPulsing = 'PrimaryPulsing',
  Secondary = 'Secondary',
  SecondaryActive = 'SecondaryActive',
  SecondaryContrast = 'SecondaryContrast',
  SecondaryClear = 'SecondaryClear',
  SecondaryClearText = 'SecondaryClearText',
  SecondaryDestructive = 'SecondaryDestructive',
  SecondaryClearActive = 'SecondaryClearActive',
  SecondaryLightHover = 'SecondaryLightHover',
  Alternative = 'Alternative',
  AlternativeSecondary = 'AlternativeSecondary', // Alternative2 in Figma
  AlternativeHeavy = 'AlternativeHeavy',
  Green = 'Green',
  Orange = 'Orange',
  Destructive = 'Destructive',
  DestructiveClear = 'DestructiveClear',
  Active = 'Active',
  Transparent = 'Transparent',
  Grey = 'Grey',
  Purple = 'Purple',
  Blue = 'Blue',
  BlueClear = 'BlueClear',
  GreenClear = 'GreenClear',
}

export enum OzContainedButtonSizePreset {
  Bare = 'Bare',
  H80px = 'H80px',
  H64px = 'H64px',
  H56px = 'H56px',
  H40px = 'H40px',
  H32px = 'H32px',
  H24px = 'H24px',
  H32pxW56px = 'H32pxW56px',
}

/**
 * It's named "contained button" following Material UI's terminology: https://material.io/components/buttons .
 *
 *
 * This component applies three types of styles:
 *
 * 1. Shape/size
 * 2. Colors
 * 3. Text truncation
 *
 *
 * To make customization easy, it's built so that you can (re)use each group of styles independently,
 * i.e. each type of style is optional. Notice that if you set `:color-scheme="null"`, `:size-preset="null"`
 * and use the default slot, it reduces to `OzBaseButton`.
 *
 */

@Component({ components: { OzBaseButton } })
export default class OzContainedButton extends Vue {
  $el!: OzBaseButton['$el']

  ColorScheme = OzContainedButtonColorScheme
  SizePreset = OzContainedButtonSizePreset

  /**
   * Dark mode.
   */
  @Prop({ default: 'auto' }) darkMode!: true | false | 'auto'

  /**
   * Whether the button is disabled.
   */
  @Prop({ default: false }) disabled!: boolean

  /**
   * Automatically uses an `<a>` tag if href is present, `<button>` otherwise.
   */
  @Prop({ default: undefined }) href?: string

  /**
   * See `OzBaseButton`.
   */
  @Prop({ default: OzBaseButtonHrefMode.Default }) hrefMode?: OzBaseButtonHrefMode

  /**
   * Color scheme.
   */
  @Prop({ default: OzContainedButtonColorScheme.Primary }) colorScheme!: OzContainedButtonColorScheme

  /**
   * Applies preset sizes/shapes.
   * They are named with pixel values for developer ergonomics -- those are the values you see in the mockups.
   * Technically, the pixel value could vary depending on the size of `rem`.
   */
  @Prop({ default: OzContainedButtonSizePreset.H40px }) sizePreset!: OzContainedButtonSizePreset

  /**
   * Text. Automatically handles truncation and adds title.
   */
  @Prop({ default: undefined }) text?: string

  /**
   * Title. If not defined, text will be used instead.
   */
  @Prop({ required: false }) title?: string

  /**
   * Adds extra padding to center the text if there's an icon.
   */
  @Prop({ default: false }) centerText?: boolean

  /**
   * Defines whether the button text should be truncated or not.
   * Use case:
   *
   * 1. `true` - truncate the button text if it exceeds the button's width
   * 2. `false` - do not truncate the button text, allowing it to stretch the button's height
   */
  @Prop({ default: true }) shouldTruncateText!: boolean

  /**
   * Defines whether the button's height should be fixed or adjusted by its content.
   */
  @Prop({ default: false }) autoHeight!: boolean

  /**
   * Tailwind uses box shadows for focus rings.
   * Didn't find a straightforward way to implement truly transparent offset color.
   * If the gap color is too different from the backdrop, customize it here.
   */
  @Prop({ default: undefined }) ringOffsetColorClasses?: Record<string, boolean>

  /**
   * Text align position
   */
  @Prop({ default: 'center' }) textAlign?: 'start' | 'center' | 'end'

  handleClick(event): void {
    /**
     * Click event.
     * @property {MouseEvent} event click event
     */
    this.$emit('click', event)
  }
}

export { OzBaseButtonHrefMode as OzContainedButtonHrefMode }
